/**
 * CandidatesPage
 *
 * Main candidates directory page. Uses shared candidate components.
 * Mirrors the LegislatorsPage layout: search, filters, bulk selection,
 * bookmark to workspace, table/card views.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  ArrowUp,
  UserCheck,
  Plus,
  Minus,
} from 'lucide-react';
import Button from '../components/Button';
import RecordCount from '../components/RecordCount';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMobile } from '../hooks/useMediaQuery';
import api from '../lib/api';
import {
  useCandidateData,
  useCandidateFiltering,
  useTrackedCandidateIds,
  toTrackedCandidateIdSet,
  applySortToData,
  CandidateFilters,
  CandidateCardGrid,
  BaseCandidate,
  ViewMode,
  CandidateRenderContext,
} from '../components/candidates';
import type { CandidateFilterState } from '../components/candidates/types';
import { normalizeDistrictLabel } from '../utils/districtDisplay';
import DataGrid from '../components/shared/DataGrid';
import { prefetchDistrictMapsForRows } from '../components/shared/dataGridCells';
import { PreviewIcon, TrackIcon } from '../components/icons';
import type { ActionsHelpIcon } from '../components/ActionsHeaderHelp';
import { buildCandidateColumns, CANDIDATE_SORT_FIELD_MAP } from '../components/candidates/candidateColumnDefs';
import { statusVariant } from '../constants/candidates';
import CandidateWorkspaceSelectionModal from '../components/CandidateWorkspaceSelectionModal';
import BulkWorkspaceSelectionModal from '../components/BulkWorkspaceSelectionModal';
import ViewToggle from '../components/ViewToggle';
import EmptyState from '../components/EmptyState';

const CandidatesPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();

  usePageTitle('Candidates', 'Browse and filter political candidates');

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('candidatesViewMode');
    return (saved === 'card' || saved === 'table') ? saved : 'table';
  });

  useEffect(() => {
    localStorage.setItem('candidatesViewMode', viewMode);
  }, [viewMode]);

  // Deep links (e.g. Returned Ballots' district "Candidates" links) seed the
  // district/office filters. Read once at mount; the filters own it after.
  const [searchParams] = useSearchParams();
  const [urlDefaultFilters] = useState<Partial<CandidateFilterState> | undefined>(() => {
    // Normalized to the canonical label ('US HD-3' → 'US HD-03'), so a
    // hand-typed or older link still matches a filter option.
    const district = normalizeDistrictLabel(searchParams.get('district')) || null;
    const office = searchParams.get('office');
    if (!district && !office) return undefined;
    return {
      ...(district ? { district } : {}),
      ...(office ? { officeSought: office } : {}),
    };
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bookmarkCandidate, setBookmarkCandidate] = useState<{ id: string; name: string } | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<'add' | 'remove'>('add');
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const {
    filters,
    sort,
    filterOptions,
    isLoadingOptions,
    setSearchQuery,
    setFilter,
    setSortDirect,
    clearFilters,
    hasActiveFilters,
  } = useCandidateFiltering({ defaultFilters: urlDefaultFilters });

  // A deep-linked district the directory doesn't know would silently empty the
  // table — drop it back to All districts once the options arrive.
  useEffect(() => {
    const district = urlDefaultFilters?.district;
    if (!district || !filterOptions?.districts) return;
    if (!filterOptions.districts.some(d => d.district === district)) {
      setFilter('district', 'All districts');
    }
  }, [filterOptions, urlDefaultFilters, setFilter]);

  const { data: candidates, isLoading, error } = useCandidateData<BaseCandidate>({
    source: 'all',
    filters,
    sort,
  });

  const { data: trackedIds, refetch: refetchTracked } = useTrackedCandidateIds();

  const trackedIdSet = useMemo(
    () => toTrackedCandidateIdSet(trackedIds),
    [trackedIds]
  );

  const { data: totalCandidateCount = 0 } = useQuery<number>(
    ['candidates-total-count'],
    async () => {
      const response = await api.get('/candidates', { params: { page: 1, limit: 1 } });
      return response.data.pagination?.total ?? response.data.data?.length ?? 0;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const sortedCandidates = useMemo(() => {
    return applySortToData(candidates, sort);
  }, [candidates, sort]);

  // Batch-resolve district maps for the table view so each row's DistrictMapLink
  // reads from cache instead of firing its own GET /district-maps (N+1).
  useEffect(() => {
    if (viewMode === 'table' && !isMobile) {
      prefetchDistrictMapsForRows(sortedCandidates);
    }
  }, [sortedCandidates, viewMode, isMobile]);

  // Handlers
  const handleSelectCandidate = useCallback((candidateId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(candidateId);
      else next.delete(candidateId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBulkAction = useCallback((actionType: 'add' | 'remove') => {
    setBulkAction(actionType);
    setShowBulkModal(true);
  }, []);

  const handleNavigate = useCallback((candidateId: string) => {
    navigate(`/candidates/${candidateId}`);
  }, [navigate]);

  const handleBookmark = useCallback((id: string, name: string) => {
    setBookmarkCandidate({ id, name });
  }, []);

  // Scroll to top
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.scrollTop > 0) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      }
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = container.scrollTop;
          break;
        }
      }
      setShowScrollToTop(Math.max(windowScrollTop, containerScrollTop) > 300);
    };

    window.addEventListener('scroll', handleScroll);
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    let mainContainer: Element | null = null;
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
        container.addEventListener('scroll', handleScroll);
        mainContainer = container;
        break;
      }
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) mainContainer.removeEventListener('scroll', handleScroll);
    };
  }, [candidates]);

  // Render context
  const context: CandidateRenderContext = useMemo(() => ({
    trackedCandidateIds: trackedIdSet,
    selectedCandidateIds: selectedIds,
    onSelectCandidate: handleSelectCandidate,
    onBookmark: handleBookmark,
    onNavigate: handleNavigate,
  }), [trackedIdSet, selectedIds, handleSelectCandidate, handleBookmark, handleNavigate]);

  // AG Grid column definitions
  const gridColumnDefs = useMemo(() => buildCandidateColumns({
    columns: ['name', 'office', 'district', 'party', 'status', 'incumbent', 'funds', 'contact', 'web', 'actions'],
    mode: 'directory',
  }), []);

  const gridContext = useMemo(() => ({
    onNavigate: handleNavigate,
    onNavigateLegislator: (id: number) => navigate(`/legislators/${id}`),
    onNavigateCandidate: (id: string) => navigate(`/candidates/${id}`),
    trackedCandidateIds: trackedIdSet,
    statusVariant,
    actions: [
      {
        id: 'view',
        icon: PreviewIcon,
        title: 'View details',
        className: 'text-text-tertiary hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary',
        onClick: (row: any) => handleNavigate(row.id),
      },
      {
        id: 'bookmark',
        icon: TrackIcon,
        title: 'Track candidate',
        className: 'text-text-tertiary hover:text-blue-600 dark:text-text-dark-tertiary',
        activeClassName: 'text-blue-600 hover:text-blue-700',
        isActive: (row: any) => trackedIdSet.has(row.id),
        onClick: (row: any) => handleBookmark(row.id, row.fullName || ''),
      },
    ],
    actionsHelpIcons: ['view', 'track'] satisfies ActionsHelpIcon[],
  }), [handleNavigate, handleBookmark, trackedIdSet, navigate]);

  const handleGridSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortDirect({ field: field as any, direction });
  }, [setSortDirect]);

  if (error) {
    return (
      <EmptyState
        icon={UserCheck}
        title="Error loading candidates"
        hint="Please try again later."
      />
    );
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Filters */}
      {isMobile ? (
        <CandidateFilters
          filters={filters}
          filterOptions={filterOptions}
          onSearchChange={setSearchQuery}
          onFilterChange={setFilter}
          onClear={clearFilters}
          hasActiveFilters={hasActiveFilters}
          config={{
            showSearch: true,
            showOffice: false,
            showParty: false,
            showDistrict: false,
            showElectionYear: false,
            showStatus: false,
            showClearButton: false,
            compact: true,
          }}
          showCard={false}
          isLoadingOptions={isLoadingOptions}
        />
      ) : (
        <CandidateFilters
          filters={filters}
          filterOptions={filterOptions}
          onSearchChange={setSearchQuery}
          onFilterChange={setFilter}
          onClear={clearFilters}
          hasActiveFilters={hasActiveFilters}
          isLoadingOptions={isLoadingOptions}
        />
      )}

      {/* View Toggle + Record Count Row */}
      <div className="flex justify-between items-center">
        <div className="hidden lg:flex items-center space-x-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <RecordCount
          label="candidates"
          showing={sortedCandidates.length}
          filtered={sortedCandidates.length}
          total={totalCandidateCount}
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">Loading candidates...</span>
        </div>
      ) : sortedCandidates.length === 0 ? (
        <EmptyState
          icon={UserCheck}
          title="No candidates found"
          hint="Try adjusting your filters or search terms."
        />
      ) : (
        <>
          {/* Bulk Selection Controls */}
          {selectedIds.size > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {selectedIds.size} candidate{selectedIds.size !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleBulkAction('add')}
                    variant="primary"
                    size="sm"
                    icon={Plus}
                  >
                    Add to Workspaces
                  </Button>
                  <Button
                    onClick={() => handleBulkAction('remove')}
                    variant="danger"
                    size="sm"
                    icon={Minus}
                  >
                    Remove from Workspaces
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Mobile: Card View Only */}
          <div className="md:hidden">
            <CandidateCardGrid
              candidates={sortedCandidates}
              context={context}
              mode="directory"
              gridCols="grid-cols-1"
            />
          </div>

          {/* Desktop: Table or Card View */}
          <div className="hidden md:block">
            {viewMode === 'table' ? (
              <DataGrid
                data={sortedCandidates}
                columnDefs={gridColumnDefs}
                gridContext={gridContext}
                getRowId={(params) => String(params.data.id)}
                sortBy={sort.field}
                sortDirection={sort.direction}
                sortFieldMap={CANDIDATE_SORT_FIELD_MAP}
                onSort={handleGridSort}
                onRowClick={(candidate) => handleNavigate(candidate.id)}
                selectable={true}
                selectedIds={selectedIds}
                onSelectionChanged={(ids) => setSelectedIds(ids)}
                emptyMessage="No candidates found"
              />
            ) : (
              <CandidateCardGrid
                candidates={sortedCandidates}
                context={context}
                mode="directory"
                gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              />
            )}
          </div>
        </>
      )}

      {/* Scroll to Top */}
      {showScrollToTop && (
        <>
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Bookmark Modal */}
      {bookmarkCandidate && (
        <CandidateWorkspaceSelectionModal
          candidateId={bookmarkCandidate.id}
          candidateName={bookmarkCandidate.name}
          isOpen={true}
          onClose={() => {
            setBookmarkCandidate(null);
            refetchTracked();
          }}
        />
      )}

      {/* Bulk Modal */}
      <BulkWorkspaceSelectionModal
        isOpen={showBulkModal}
        onClose={() => {
          setShowBulkModal(false);
          clearSelection();
          refetchTracked();
        }}
        entityType="candidate"
        entityCount={selectedIds.size}
        actionType={bulkAction}
        onSave={async (workspaceIds) => {
          await api.post('/workspace-candidate-tracking/bulk', {
            candidateIds: Array.from(selectedIds),
            workspaceIds,
            action: bulkAction,
          });
        }}
      />
    </div>
  );
};

export default CandidatesPage;
